/**
 * Styled Components Utilities
 *
 * Provides utilities for MUI's styled function to properly filter transient props.
 * MUI's styled doesn't automatically filter $-prefixed props like styled-components does.
 */

/**
 * Filter function to prevent $-prefixed props from being forwarded to the DOM.
 * This is required because MUI's styled function doesn't automatically filter
 * transient props like styled-components does.
 *
 * @param prop - The prop name to check
 * @returns true if the prop should be forwarded to the DOM, false otherwise
 *
 * @example
 * ```tsx
 * const StyledDiv = styled('div', {
 *   shouldForwardProp: shouldForwardTransientProp,
 * })<{ $isActive: boolean }>(({ $isActive }) => ({
 *   color: $isActive ? 'blue' : 'gray',
 * }));
 * ```
 */
export const shouldForwardTransientProp = (prop: PropertyKey): boolean => {
  return !prop.toString().startsWith('$');
};

/**
 * Options object that can be spread into styled() to filter transient props.
 *
 * @example
 * ```tsx
 * import { styled } from '@mui/material/styles';
 * import { transientOptions } from '../../utils/styledUtils';
 *
 * const StyledDiv = styled('div', transientOptions)<{ $color: string }>(
 *   ({ $color }) => ({ color: $color })
 * );
 * ```
 */
export const transientOptions = {
  shouldForwardProp: shouldForwardTransientProp,
} as const;

import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Safely compose a component-base `sx` with a consumer-supplied `sx` prop.
 *
 * MUI's `SxProps<Theme>` can be:
 *   • an object — `{ color: 'red' }`
 *   • an array — `[{ color: 'red' }, condition && { fontWeight: 700 }]`
 *   • a theme callback — `(theme) => ({ color: theme.palette.error.main })`
 *
 * Object spread (`{ ...base, ...sx }`) only handles the FIRST case — array
 * sx lands on the resulting object as numeric keys (`0`, `1`, `2`…) and
 * theme-callback sx becomes a function-as-value that MUI ignores. The
 * MUI-canonical composition is the array form `sx={[base, ...sxAsArray]}`,
 * which the runtime walks correctly for every legal SxProps shape.
 *
 * @example
 * ```tsx
 * <Box sx={composeSx(BASE_SX, sx)}>
 * ```
 *
 * @param base - The component's own base styles (always object form).
 * @param sx - The consumer-supplied `sx` prop (any legal SxProps shape).
 * @returns SxProps array that MUI composes correctly.
 */
export function composeSx<TBase extends object>(base: TBase, sx?: SxProps<Theme>): SxProps<Theme> {
  if (sx == null) return base as SxProps<Theme>;
  return [base, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>;
}

export default shouldForwardTransientProp;
